import { Form, Checkbox, Typography, Flex } from "antd";
import CustomInput from "../../components/CustomInput/Input";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { useSelector, useDispatch } from "react-redux";
import { useState } from "react";
import EntityTagSelector from "../QuestionBank/EntityTagSelector";
import OptionsBuilder from "../QuestionBank/OptionsBuilder";
import CustomButton from "../../components/CustomButton/Button";
import { BTN_TXT } from "../../constants/constants";
import {
  addQuestion,
  getFilteredQuestions,
  getMappedQuestions,
} from "../../redux/slices/QuestionBankSlice";

const { Text } = Typography;

const AddQuestionForm = ({ onClose, orgId, dealId, dealCategoryId, entityTypeId }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [isAddToTemplate, setIsAddToTemplate] = useState(false);
  const { dropdownData } = useSelector((state) => state.questionBank);
  const questionType = Form.useWatch("questionTypeId", form);
  const selectedCategory = Form.useWatch("categoryId", form);

  const SHOW_PLACEHOLDER_TYPES = [
    "QTM1000001",
    "QTM1000002",
    "QTM1000003",
    "QTM1000005",
    "QTM1000006",
    "QTM1000007",
  ];

  const OPTION_TYPES = ["QTM1000005", "QTM1000006", "QTM1000007"];

  const FILE_UPLOAD_TYPE = "QTM1000008";

  const handleFinish = async (values) => {
    const payload = {
      ...values,
      isRequired: values.isRequired || false,
      fileType: values.fileType
        ? values.fileType.split(",").map((t) => t.trim())
        : undefined,
      entityTags: values.entityTags?.filter((tag) => tag !== "ALL") || [],
      ...(dealCategoryId && { dealCategoryId }),
      ...(!isAddToTemplate && orgId && { orgId }),
    };

    const refreshParams = {
      orgId,
      ...(dealId && { dealId }),
      ...(dealCategoryId && { dealCategoryId }),
      ...(entityTypeId && { entityTypeId }),
    };

    const refreshQuestionLists = () => {
      dispatch(getFilteredQuestions(refreshParams));
      if (entityTypeId && dealCategoryId) {
        dispatch(
          getMappedQuestions({
            entityTypeId,
            dealCategoryId,
            orgId,
            ...(dealId && { dealId }),
          }),
        );
      }
    };

    if (isAddToTemplate) {
      const res = await dispatch(addQuestion(payload));

      if (res?.payload?.statusCode === 201) {
        refreshQuestionLists();
        form.resetFields();
        onClose();
      }
    } else {
      const res = await dispatch(addQuestion(payload));

      if (res?.payload?.statusCode === 201) {
        refreshQuestionLists();
        form.resetFields();
        onClose();
      }
    }
  };

  const handleSaveToTemplate = () => {
    setIsAddToTemplate(true);
    form.submit();
  };

  const handleSaveThisBusinessOnly = () => {
    setIsAddToTemplate(false);
    form.submit();
  };

  return (
    <div className="question-bank">
      <Form
        layout="vertical"
        className="add-question-form"
        form={form}
        onFinish={handleFinish}
      >
        {/* Category */}
        <CustomSelect
          label="Question Category"
          name="categoryId"
          isRequired
          placeholder="Select a category"
          rules={[{ required: true, message: "Please select category" }]}
          options={[
            ...(dropdownData?.questionCatogory?.map((category) => ({
              value: category.categoryId,
              label: category.categoryName,
            })) || []),
            { value: "OTHERS", label: "Others" },
          ]}
        />

        {selectedCategory === "OTHERS" && (
          <CustomInput
            label="Custom Category Name"
            name="customCategoryName"
            isRequired
            placeholder="Enter custom category name"
            rules={[
              { required: true, message: "Please enter custom category name" },
            ]}
          />
        )}

        {/* Question Type */}
        <CustomSelect
          label="Question Type"
          name="questionTypeId"
          isRequired
          placeholder="Select question type"
          rules={[{ required: true, message: "Please select question type" }]}
          options={dropdownData?.questionType?.map((type) => ({
            value: type.questionTypeId,
            label: type.questionTypeName,
          }))}
        />

        {/* Question Title */}
        <CustomInput
          label="Question Title"
          name="questionTitle"
          isRequired
          placeholder="Enter question text"
          rules={[{ required: true, message: "Please enter question title" }]}
        />

        {/* Description */}
        <CustomInput
          label="Description"
          name="description"
          placeholder="Add helper text or contextual notes"
          isTextArea
        />

        {SHOW_PLACEHOLDER_TYPES.includes(questionType) && (
          <CustomInput
            label="Placeholder Text (Optional)"
            name="placeholder"
            placeholder="Enter placeholder text"
          />
        )}

        {OPTION_TYPES.includes(questionType) && (
          <Form.Item
            label={
              <span>
                Options<Text type="danger"> *</Text>
              </span>
            }
            name="options"
            className="entity-tag-form-item"
            rules={[{ required: true, message: "Add at least one option" }]}
          >
            <OptionsBuilder />
          </Form.Item>
        )}

        {questionType === FILE_UPLOAD_TYPE && (
          <CustomInput
            label="Accepted File Types (Separate multiple types with commas e.g. .pdf,.zip)"
            name="fileType"
            placeholder="e.g. .pdf, .docx, .jpg"
          />
        )}

        {/* Required */}
        <Form.Item name="isRequired" valuePropName="checked">
          <Checkbox>Mark as Required Field</Checkbox>
        </Form.Item>

        {/* Entity Tags */}
        <Form.Item
          label={
            <span>
              Company Type Tags<Text type="danger"> *</Text>
            </span>
          }
          name="entityTags"
          className="entity-tag-form-item"
          rules={[
            { required: true, message: "Select at least one company type" },
          ]}
        >
          <EntityTagSelector
            options={[
              ...(dropdownData?.entityType?.map((e) => ({
                value: e.entityTypeId,
                label: e.entityTypeName,
              })) || []),
              { value: "ALL", label: "All" },
            ]}
          />
        </Form.Item>
        <Flex
          align="center"
          gap="10px"
          style={{
            position: "sticky",
            bottom: 0,
            padding: "10px 0px",
            background: "#fff",
          }}
        >
          <CustomButton type="primary" block onClick={handleSaveToTemplate}>
            {BTN_TXT.SAVE_QUESTION_PUBLIC}
          </CustomButton>
          <CustomButton
            type="default"
            block
            onClick={handleSaveThisBusinessOnly}
          >
            {BTN_TXT.SAVE_QUESTION_BUS}
          </CustomButton>
        </Flex>
      </Form>
    </div>
  );
};

export default AddQuestionForm;
